import { useEffect, useState, useCallback, useMemo } from 'react';
import FilterSearch from '../../../components/FilterSearch/FilterSearch';
import { Badge, Button, Spinner, Table } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import DHPagination from '../../../components/Pagination';
import { getTechniciansList } from '../ManageServiceOrders/actions/action';
import { fetchTechniciansList } from './actions/action';

const TechnicianList = ({ onEdit, onView, technicianList, isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [toListData, setToListData] = useState([]);
  // State for FilterSearch
  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [statusFilter, setStatusFilter] = useState({ status: 'All' });
  const [advancedFilter, setAdvancedFilter] = useState({});
    

  const [isDataLoading, setDataLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    let list = [];
    if (technicianList && Array.isArray(technicianList)) {
      list = technicianList;
    }

    // Group by to_number and take the first occurrence
    const grouped = list.reduce((acc, current) => {
      const existing = acc.find(item => item.emp_id === current.emp_id);
      if (!existing) {
        acc.push({ ...current, total_items: 1 });
      } else {
        existing.total_items += 1;
      }
      return acc;
    }, []);

    const sorted = grouped.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setTotalRecords(sorted.length);

    const page = Number(paginationValue.page) || 0;
    const limit = Number(limitValue.limit) || 10;
    const paginated = sorted.slice(page * limit, (page + 1) * limit);

    setToListData(paginated);
  }, [technicianList, paginationValue.page, limitValue.limit]);

  const getTechniciansList = useCallback(
    async (data) => {
      try {
        setDataLoading(true);
        await fetchTechniciansList({
          params: data,
          dispatch,
        });
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setDataLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    // Only reset to page 0 if filters actually changed to a new state
    // We can omit setPaginationValue from here to avoid loops if the hook isn't stable
    setPaginationValue({ page: 0 });
  }, [searchFilter, statusFilter, advancedFilter, limitValue.limit]);


  useEffect(() => {
    const delay = setTimeout(() => {
      getTechniciansList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        sort: {
          value: 'id',
          order: 1,
        },
        search_qry: searchFilter?.search,
        product_type: statusFilter?.status,
        advancedFilter,
      });
    }, 300);
    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue.limit,
    searchFilter,
    statusFilter,
    advancedFilter,
    getTechniciansList,
  ]);


  return (
    <>
      <FilterSearch
        setAdvancedFilter={setAdvancedFilter}
        setSearchFilter={setSearchFilter}
        setStatusFilter={setStatusFilter}
        filterType="toList"
      />
      <div className="dt-table">
        <div className="table-responsive">
          <Table className="OrderListTable table mb-0">
            <thead>
              <tr className="align-middle">
                <th>SI.No</th>
                <th>EMP ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Department</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            {isLoading || isDataLoading ? (
              <tbody>
                {[...Array(2)].map((_, index) => (
                  <tr key={index} className="align-middle">
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-2"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow d-flex flex-column gap-1">
                        <span className="placeholder col-6"></span>
                        <span className="placeholder w-75"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                     <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                     <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                     <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td className="text-center">
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : toListData?.length > 0 ? (
              toListData?.map((item, index) => (
                <tbody
                  key={index}
                  className={`${
                    item?.isAmc && item?.isAmc === '1' ? 'bg-soft-light' : ''
                  }`}
                >
                  <tr className="align-middle">
                    <td>{(Number(paginationValue.page) * Number(limitValue.limit)) + index + 1}</td>
                    <td>
                      <div className="medium">
                        <b>EMP ID:</b> <span className="badge bg-soft-info text-info">{item?.emp_id}</span><br />
                        <b>Created on:</b> {item?.created_at ? item.created_at.substring(0, 10) : ''}
                      </div>
                    </td>
                    <td>
                      <div className="medium">
                        {item?.technician_name || 'N/A'}
                      </div>
                    </td>
                    <td>
                      {item?.email}
                    </td>
                    <td>
                      {item?.phone}
                    </td>
                    <td className='text-capitalize'>
                      {item?.department}
                    </td>
                    <td className='text-capitalize'>
                      {item?.location}
                    </td>
                    <td>
                      <Badge color={item?.status === 'active' ? 'success' : 'danger'} className='text-capitalize'>{item?.status}</Badge>
                    </td>
                    <td className="text-center">
                        <a
                          type='button'
                          onClick={() => onEdit(item)}
                          className="btn btn-sm btn-outline-warning waves-effect waves-light"
                        >
                          <i className="mdi mdi-pencil"></i>
                        </a>
                        <a
                          type='button'
                          onClick={() => onView(item)}
                          className="btn btn-sm btn-outline-info waves-effect waves-light ms-2"
                        >
                          <i className="mdi mdi-eye"></i>
                        </a>
                    </td>
                  </tr>
                </tbody>
              ))
            ) : (
              <tbody>
                <tr>
                  <td colSpan={8}>No Records found</td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>

        {totalRecords > 0 ? (
          <DHPagination
            totalPages={Math.ceil(totalRecords / Number(limitValue.limit || 10))}
            currentPage={Number(paginationValue.page) || 0}
            setPaginationValue={setPaginationValue}
            setLimitValue={setLimitValue}
          />
        ) : null}
      </div>
    </>
  );
};

export default TechnicianList;
